<template>
  <div class="payment-stripe-card-component">
    <p class="card-title" v-text="t('payment.card.enter_card_details')" />
    <div>
      <label class="card-label" v-text="t('payment.card.card_details')" />
      <div class="cart-details" :class="{ error: hasError, success: hasSuccess }">
        <div ref="card" />
      </div>
      <div ref="error" class="card-errors"></div>
    </div>
    <template v-if="stripeError">
      <Alert danger>{{ stripeError }}</Alert>
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { FrontendPaymentProcessor } from '~/types/payment/paymentFrontedTypes'
import type { StripeEventInterface, StripePaymentMethodInterface } from '~/types/stripeTypes'
import { useBalanceStore } from '~/store/balance'

const { t } = useLang()

const balance = useBalanceStore()
const localeSetting = useState<string>('locale.setting')
const { paymentMethod } = defineProps<{
  paymentMethod: FrontendPaymentProcessor
}>()

const card = ref<HTMLElement>()

const colorMode = useColorMode()

const style = computed(() => ({
  base: {
    fontSize: '0.875rem',
    color: colorMode.value === 'dark' ? '#F3F3F3' : '#454545',
    fontFamily: 'Roboto, sans-serif',
    fontWeight: '500',
    iconColor: colorMode.value === 'dark' ? '#D1D5DB' : '#212121',
    '::placeholder': {
      color: colorMode.value === 'dark' ? '#9CA3AF' : '#212121',
      fontWeight: 'normal',
    },
  },
  complete: {
    iconColor: '#50C94F',
    color: '#50C94F',
  },
  invalid: {
    iconColor: colorMode.value === 'dark' ? '#EF4444' : '#EA0000',
    color: '#EA0000',
  },
}))

const stripeError = ref<string | null>(null)
const hasError = ref<boolean>(false)
const hasSuccess = ref<boolean>(false)

const stripeInstance = ref<any>(null)
const stripeCard = ref<any>(null)
let changeEventListener: ((event: StripeEventInterface) => void) | null = null

const mountStripeInstance = () => {
  const stripe = (window as any).Stripe(paymentMethod.public_key, { locale: localeSetting.value })
  stripeInstance.value = stripe
  const elements = stripe.elements()

  stripeCard.value = elements.create('card', { style })
  stripeCard.value.mount(card.value)
}

onMounted(() => {
  if (card.value) {
    mountStripeInstance()

    changeEventListener = (event: StripeEventInterface) => processCardData(event, stripeInstance.value)
    stripeCard.value.addEventListener('change', changeEventListener)
  }
})

watch(
  () => colorMode.value,
  () => {
    if (card.value) {
      // Clean up previous event listener before mounting new instance
      if (stripeCard.value && changeEventListener) {
        stripeCard.value.removeEventListener('change', changeEventListener)
      }
      mountStripeInstance()
      // Re-attach event listener after mounting
      if (stripeCard.value) {
        changeEventListener = (event: StripeEventInterface) => processCardData(event, stripeInstance.value)
        stripeCard.value.addEventListener('change', changeEventListener)
      }
    }
  }
)

onBeforeUnmount(() => {
  if (stripeCard.value && changeEventListener) {
    stripeCard.value.removeEventListener('change', changeEventListener)
  }
})

function processCardData(event: StripeEventInterface, stripe: any) {
  stripeError.value = null
  hasError.value = false
  hasSuccess.value = false
  if (event.error) {
    hasError.value = true
    stripeError.value = event.error.message
  } else if (event.complete) {
    hasSuccess.value = true
    ;(stripe as any)
      .createPaymentMethod({
        type: 'card',
        card: balance.stripe.card,
      })
      .then(function (result: StripePaymentMethodInterface) {
        const stripeToken = result.paymentMethod?.id || null
        balance.setStripeToken(stripeToken)
      })
  }
}
</script>

<style lang="postcss" scoped>
.payment-stripe-card-component {
  @apply flex flex-col gap-4 border border-neutral6 dark:border-dmGray1 bg-white dark:bg-dmGray1 rounded-sm xl:rounded-md p-2 xl:p-5;
  .card-title {
    @apply font-medium text-sm text-text1 dark:text-dmText1;
  }
  .card-label {
    @apply text-xs text-text2 dark:text-dmText2;
  }
  .cart-details {
    @apply relative flex flex-col items-center justify-center border border-neutral6 dark:border-dmGray1 rounded-sm h-[2.75rem] py-2 px-4
    transition-[border];
    > div {
      @apply w-full;
    }
    &.error {
      @apply border-red;
    }
    &.success {
      @apply border-greenDark;
    }
  }
}
.alt {
  .payment-stripe-card-component,
  .cart-details {
    @apply rounded-sm;
  }
}
</style>
